import type { ReactNode } from "react";
import { Check, EyeOff, Flag, Pin, PinOff, Trash2, TriangleAlert } from "lucide-react";
import { GUIDELINES, MOD_ITEM_NOUNS } from "../livingRoom.js";
import { MemberDoor } from "./MemberDoor";

// 13-F2 — **ONE ROW OF HER MODERATION QUEUE, AND IT IS THE ROW SHE DECIDES FROM.**
//
// It came out of `OwnerLivingRoom.tsx` for the reason every extraction in this repo has had: the
// parent holds seven hooks, so nothing could CALL it and the only instrument that could reach this
// row was a real Chrome signed in as the owner with something already held in the queue. Out here it
// is props in and tree out, and `scripts/item13-f2-queue-title.mjs` calls it once per kind of thing
// she judges and reads the tree back.
//
// **WHAT 13-F2 FIXED: A POST'S TITLE WAS NEVER DRAWN HERE.** `moderationQueue` has always SELECTed
// `t.title` and shipped it; this row rendered the body and nothing else. `handleCreatePost`'s own
// comment says why that matters — the title is "the most public string a post owns: the feed shows
// it and the body it belongs to may never be opened", which is exactly why the gate screens the
// title WITH the body. So she was approving, hiding, deleting and warning while looking at the half
// a passer-by may never read.
//
// **IT SITS ABOVE THE BODY RATHER THAN LEADING THE HEADER, AND THAT IS A CONSERVATIVE READING**
// flagged for her word (DECISIONS.md): the header is WHO and WHAT (a member, a noun, a time) and the
// title is CONTENT — it was screened with the body, so it is drawn with the body. One line to move.

export type ModIdentity = {
  ref: string; name: string; avatar: string; owner: boolean; badge: string | null;
  fixture?: boolean; photo?: string | null;
};
export type ModItem = {
  id: string; body: string; author: ModIdentity; createdAt: string; likes: number;
  // 13-F2 — the post's own title. Optional, and it must stay optional: the room has required a title
  // on compose since LR1-12 round two, but posts written before that have none, and a comment or a
  // photo never had one. An absent title draws NOTHING (T9-2's law: no body, no box).
  title?: string | null;
  heldReason?: string | null; actualVisibility?: string; hiddenBy?: string; reports?: number;
  reportNotes?: string | null; reportRead?: string | null;
  postId?: string; comments?: number; pinned?: boolean;
  reviewed?: boolean; edge?: number; sizeBytes?: number;
};
export type ModAction = { key: string; label: string; verb: string; note: string };
type Guideline = { code: string; rule: string; body: string };

// ReactNode, not JSX.Element: React 19 removed the global JSX namespace, and reaching for it is a
// guaranteed TS2503 that the build (which does not type-check) would happily ship past.
const ICON: Record<string, ReactNode> = {
  approve: <Check size={14} />, hide: <EyeOff size={14} />, delete: <Trash2 size={14} />, warn: <TriangleAlert size={14} />,
};
export const reasonLabel = (code: string | null | undefined) =>
  (GUIDELINES as Guideline[]).find((g) => g.code === code)?.rule || (code ? String(code) : "");
export const stamp = (iso: string) => (iso ? new Date(iso).toLocaleString() : "");

export function ModerationRow({
  item, actions, onOpenMember, onAct, onPin,
}: {
  item: ModItem & { kind: "post" | "comment" | "avatar" };
  actions: ModAction[];
  onOpenMember?: (id: string) => void;
  onAct: (action: string) => void;
  onPin: (pinned: boolean) => void;
}) {
  const held = item.actualVisibility === "hidden";
  return (
    <article className={item.author.fixture ? "owner-lr__item is-fixture" : "owner-lr__item"}>
      <header>
        {/* D6-4 — a member's name IS A DOOR, in her seat too: from a held post she can be in
            that member's file (usage, grants, refunds, the service log) without leaving the
            queue. It rides `ref`, the opaque handle the room already carries for reporting
            and blocking — the DISPLAYED name is still the room first-name and nothing else. */}
        <strong>
          {onOpenMember && item.author.ref
            ? <MemberDoor id={item.author.ref} name={item.author.name} onOpen={onOpenMember} />
            : item.author.name}
        </strong>
        {/* HER FIXTURE CONTENT LAW: a harness account is labelled, loudly and in a colour that
            is not the house gold, so a synthetic test string can never read as a member. */}
        {item.author.fixture ? <em className="owner-lr__fixture">fixture</em> : null}
        {/* The NOUN her seat reads, from the same table the confirm sentence composes with —
            "photo", never the `avatar` key the code uses. */}
        <span className="owner-lr__kind">{MOD_ITEM_NOUNS[item.kind] || item.kind}</span>
        <span className="owner-lr__when">{stamp(item.createdAt)}</span>
        {item.reports ? <em className="owner-lr__flag"><Flag size={11} />{item.reports}</em> : null}
      </header>
      {/* LR1-15 — HELD CONTENT SHOWS GRAYED, WITH ITS REASON, and "Approve" is the one-tap
          RESTORE: it becomes publicly visible as if it was never held, and the author never
          knew. The reason is an owner-only field; it exists in this payload and in no other. */}
      {held ? (
        <p className="owner-lr__held">Held by the {item.hiddenBy} gate: {reasonLabel(item.heldReason)}</p>
      ) : null}
      {/* ITEM 4(c) — WHAT SHE IS ACTUALLY JUDGING IS THE PICTURE, so the picture is the body.
          It is rendered at a size a face can be read at rather than the 40px it wears in the
          room: this is the one screen where the point is to look at it properly. A photo she
          has already hidden still shows here — her seat sees held content, which is the same
          rule that puts a held post in this queue at all. */}
      {item.kind === "avatar" ? (
        <div className={held ? "owner-lr__photo is-held" : "owner-lr__photo"}>
          {item.author.photo
            ? <img src={item.author.photo} alt={`${item.author.name}'s profile photo`} />
            : <em>(the photo is gone)</em>}
          <span className="owner-lr__photometa">
            {item.reviewed ? "Looked at" : "New, nobody has looked at it"}
            {item.edge ? ` · ${item.edge}px` : ""}
            {item.sizeBytes ? ` · ${Math.max(1, Math.round(item.sizeBytes / 1024))}KB` : ""}
          </span>
        </div>
      ) : (
        <>
          {/* 13-F2 — THE TITLE, WITH THE BODY, BECAUSE THE GATE JUDGED THEM TOGETHER. It wears the
              same held styling as the body: a held post whose title read normal-weight-white would
              say the hold applied to half of it. */}
          {item.title ? (
            <p className={held ? "owner-lr__title is-held" : "owner-lr__title"}>{item.title}</p>
          ) : null}
          <p className={held ? "owner-lr__body is-held" : "owner-lr__body"}>
            {item.body || <em>(no text)</em>}
          </p>
        </>
      )}
      {/* LR1-12 ROUND THREE: a member taps ONE flag and says nothing about which rule. The
          classification is ours, and this is where it lands: what the gate makes of the
          flagged item, plus her words if she chose to leave any. */}
      {item.reports ? (
        <p className="owner-lr__read"><strong>The gate reads it as:</strong> {item.reportRead || "nothing against the rules"}</p>
      ) : null}
      {item.reportNotes ? <p className="owner-lr__note">{item.reportNotes}</p> : null}
      <footer>
        {actions.map((a) => (
          <button key={a.key} type="button" className="owner-link" title={a.note} onClick={() => onAct(a.key)}>
            {ICON[a.key]}{a.label}
          </button>
        ))}
        {item.kind === "post" ? (
          <button type="button" className="owner-link" onClick={() => onPin(!item.pinned)}>
            {item.pinned ? <><PinOff size={14} />Unpin</> : <><Pin size={14} />Pin</>}
          </button>
        ) : null}
      </footer>
    </article>
  );
}
